import { createRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import ActionButton from "../../formInputs/buttons/ActionButton";
import CommentModal from "./CommentModal";
import LoadingSvg2 from "../../loadingSvg/LoadingSvg2";
import RatingGridBody from "./RatingGridBody";
import FluencyWpmField from "../../teacher/singleStudentPage/Ratings/FluencyWpmField";
import SafeMathHtml from "../../shared/SafeMathHtml";
import SortHeader from "../../shared/SortHeader";
import StudentImage from "../../shared/StudentImage";
import useScrollSync from "../../../hooks/useScrollSync";
import useSortConfig from "../../../hooks/useSortConfig";
import useStudentImages from "../../../hooks/useStudentImages";

import { compareGroupNameColumn, groupNameNormalizer } from "../../../utils/GroupNameNormalizer";
import { buildContentItems } from "../../../utils/teacher/StudentPageUtils";
import { getGradingCriteriaId, RATING_DISPLAY_LABELS, RATING_OPTIONS } from "../../../utils/teacher/InputRatingUtils";
import isEprEditable from "../../../utils/ActionControls/isEprEditable";
import isGradingProfileWindowOpen from "../../../utils/ActionControls/isGradingProfileWindowOpen";
import useFluencyWpm from "../../../hooks/useFluencyWpm";
import { getItemStem, resolveFluencyGradeName, sanitizeBenchmarkValue } from "../../../utils/fluencyWpm/stems";

import "../../../styles/RatingTable.scss";

/**
 * RatingGrid — purely presentational batch rating table.
 *
 * Owns only layout concerns:
 *   - Scroll sync between fixed student columns and scrollable rating columns
 *   - Column scroll-to-active on Prev/Next navigation
 *   - Active grading window detection
 *   - Comment modal open/close state (the modal itself is presentational)
 *
 * All rating/comment state and save logic live in the parent
 * (HomeroomRatings or GroupRatings) via useRatingState / useCommentState /
 * useRatingSave / useCommentSave.
 *
 * All handlers that mutate state are received as props.
 */
const RatingGrid = ({
    actionWindowsDto,
    basePath,
    comments,
    fluencyByKey,
    existingRatingsData,
    groupData,
    hasChanges,
    /** True when subject comments and/or fluency WPM have unsaved edits. */
    hasCommentChanges,
    mode,
    onRatingChange,
    onBulkRating,
    onCommentChange,
    onFluencyChange,
    onSave,
    onSaveComments,
    onSaveComment,
    ratings,
    ratingsLoaded,
    selectedEpr,
    /** Grade that drives fluency catalog/stem (student/shared grade, not teacher EPR). */
    fluencyGradeName,
    selectedSchoolLocationKey,
    selectedTemplate,
    token,
    userDetails
}) => {
    const navigate = useNavigate();

    // ── Local UI state (layout only) ───────────────────────────────────────────
    const [activeColumnIndex, setActiveColumnIndex] = useState(0);
    const [isActiveWindow, setIsActiveWindow] = useState(false);
    const [showComments, setShowComments] = useState(false);
    const [commentModalOpen, setCommentModalOpen] = useState(false);
    const [activeCommentContext, setActiveCommentContext] = useState(null);

    const { sortConfig, requestSort } = useSortConfig(mode === "GROUPS" ? "groupName" : "lastName");

    const fixedRef = useRef(null);
    const scrollRef = useRef(null);
    const columnRefs = useRef([]);

    // ── Derived data ───────────────────────────────────────────────────────────
    const schoolYearKey = selectedEpr?.schoolYearKey ?? groupData?.[0]?.schoolYearKey;

    const derivedExistingRatingsData = useMemo(() => {
        if (mode === "GROUPS" && groupData?.length) {
            return groupData.flatMap((group) =>
                group.groupStudentContents.map((student) => ({
                    key: `${group.groupId}-${student.gradingProfileStudentId}`,
                    comment: null,
                    contentRatings: student.contentRatings || [],
                    contentStudentComments: student.contentStudentComments || null,
                    gradingProfile: group.gradingProfileId,
                    gradingProfileStatus: group.gradingProfileStatus,
                    sectionId: student.sectionId,
                    studentNumber: student.studentNumber,
                    teacherName: group.teacherName || ""
                }))
            );
        }
        return existingRatingsData;
    }, [existingRatingsData, groupData, mode]);

    const contentItems = useMemo(() => buildContentItems(selectedTemplate), [selectedTemplate]);
    const resolvedFluencyGrade = resolveFluencyGradeName({
        selectedGrade: fluencyGradeName,
        eprGradeName: selectedEpr?.gradeName
    });
    const { items: fluencyItems } = useFluencyWpm({
        gradeName: resolvedFluencyGrade,
        subjectName: selectedTemplate?.subjectName
    });
    const fluencyOffset = fluencyItems.length;

    // Deduplicate students — prefer EDIT-permission instance for each student
    const students = useMemo(() => {
        const flat =
            mode === "GROUPS"
                ? (groupData || []).flatMap((group) =>
                      group.groupStudentContents.map((s) => ({
                          key: `${group.groupId}-${s.gradingProfileStudentId}`,
                          contentRatings: s.contentRatings || [],
                          contentStudentComments: s.contentStudentComments || null,
                          firstName: s.firstName,
                          gradingProfile: group.gradingProfileId,
                          gradingProfileStatus: group.gradingProfileStatus,
                          gradingProfileStudentId: s.gradingProfileStudentId,
                          groupHomeroomTeacher: group.teacherName,
                          groupId: group.groupId,
                          groupName: group.groupName,
                          groupPermission: group.permission,
                          isShared: !!group.shareGroupId,
                          lastName: s.lastName,
                          sectionId: s.sectionId,
                          studentNumber: s.studentNumber
                      }))
                  )
                : (selectedEpr?.gradingProfileStudents || []).map((s) => {
                      const entry = existingRatingsData?.find((e) => e.key === s.key) || {};
                      return {
                          key: s.key,
                          contentRatings: entry.contentRatings || [],
                          contentStudentComments: entry.contentStudentComments || null,
                          firstName: s.firstName,
                          gradingProfile: selectedEpr.key,
                          gradingProfileStudentId: s.key,
                          lastName: s.lastName,
                          sectionId: s.sectionId,
                          studentNumber: s.studentNumber
                      };
                  });

        const seen = new Map();
        flat.forEach((s) => {
            if (!seen.has(s.studentNumber)) seen.set(s.studentNumber, []);
            seen.get(s.studentNumber).push(s);
        });

        const result = [];
        seen.forEach((instances) => {
            const editable = instances.find((s) => s.groupPermission !== "VIEW");
            result.push({ ...(editable || instances[0]), isDisabled: !editable });
        });

        return result.sort((a, b) => {
            if (mode === "GROUPS" && sortConfig.key === "groupName") {
                return compareGroupNameColumn(a, b, sortConfig.direction);
            }
            const aVal = a[sortConfig.key]?.toLowerCase?.() || "";
            const bVal = b[sortConfig.key]?.toLowerCase?.() || "";
            return sortConfig.direction === "ascending" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        });
    }, [groupData, selectedEpr, existingRatingsData, mode, sortConfig]);

    const { images: studentImages, loading: imagesLoading } = useStudentImages(students, schoolYearKey, token);

    // ── Permission flags ───────────────────────────────────────────────────────
    const viewOnlyGroupExists = groupData?.some((g) => g.permission === "VIEW");
    const onlyViewOnly = mode === "GROUPS" && groupData?.every((g) => g.permission === "VIEW");
    const effectiveStatus = selectedEpr?.status ?? groupData?.find((g) => g.gradingProfileStatus)?.gradingProfileStatus;
    const anyReleasedEprs =
        effectiveStatus === "READY_FOR_RELEASE" ||
        students?.some((s) => s.gradingProfileStatus === "READY_FOR_RELEASE");

    // ── Grading window ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (!actionWindowsDto) return;
        const timePeriod = mode === "GROUPS" ? groupData?.[0]?.timePeriod : selectedEpr?.timePeriod;
        if (timePeriod) setIsActiveWindow(isGradingProfileWindowOpen(actionWindowsDto, timePeriod));
    }, [actionWindowsDto, groupData, mode, selectedEpr]);

    // ── Scroll sync ────────────────────────────────────────────────────────────
    useScrollSync(fixedRef, scrollRef, [imagesLoading]);

    // ── Regenerate column refs when content items change ──────────────────────
    useEffect(() => {
        columnRefs.current = Array.from({ length: contentItems.length + fluencyOffset }, () => createRef());
    }, [contentItems, fluencyOffset]);

    // ── Horizontal scroll to active column ────────────────────────────────────
    useEffect(() => {
        const el = columnRefs.current[activeColumnIndex]?.current;
        const container = scrollRef.current;
        if (!el || !container) return;
        const left = el.offsetLeft - container.offsetLeft;
        const right = left + el.offsetWidth;
        if (left < container.scrollLeft) {
            container.scrollTo({ left: left - 20, behavior: "smooth" });
        } else if (right > container.scrollLeft + container.clientWidth) {
            container.scrollTo({ left: right - container.clientWidth + 20, behavior: "smooth" });
        }
    }, [activeColumnIndex]);

    // ── Handlers ───────────────────────────────────────────────────────────────
    const handleToggleComments = () => {
        if (hasChanges) onSave();
        if (showComments && onSaveComments) onSaveComments();
        setShowComments((prev) => !prev);
    };

    const handleOpenStudent = useCallback(
        (student) => {
            const preselectedGroupIds = Array.isArray(groupData) ? groupData.map((r) => String(r.groupId)) : [];
            navigate(
                {
                    pathname: `${basePath || "/home"}/summary/input-ratings/student/${student.studentNumber}`,
                    search: selectedTemplate?.templateId ? `?templateId=${selectedTemplate.templateId}` : ""
                },
                { state: { mode, sharedGroupId: student.groupId, preselectedGroupIds, selectedSchoolLocationKey } }
            );
        },
        [basePath, groupData, mode, navigate, selectedSchoolLocationKey, selectedTemplate]
    );

    const openCommentModal = useCallback(
        (student, contentItem) => {
            if (hasChanges) onSave();
            const ratingKey = `${student.key}_${contentItem.templateContentId}`;
            const existingRating = derivedExistingRatingsData
                .find((r) => r.key === student.key)
                ?.contentRatings?.find((r) => r.templateContentId === contentItem.templateContentId);
            setActiveCommentContext({
                student,
                contentItem,
                rating: ratings[ratingKey] || existingRating?.rating || "",
                comment: existingRating?.comment || "",
                key: existingRating?.key
            });
            setCommentModalOpen(true);
        },
        [derivedExistingRatingsData, hasChanges, onSave, ratings]
    );

    const handleOpenCommentModal = useCallback(
        (student, item) => {
            const isNew = !student.contentRatings.some((cr) => cr.templateContentId === item.templateContentId);
            if (isNew && hasChanges) return;
            openCommentModal(student, item);
        },
        [hasChanges, openCommentModal]
    );

    const closeCommentModal = () => {
        setCommentModalOpen(false);
        setActiveCommentContext(null);
    };

    const getFluencyWpmForStudent = useCallback(
        (student) => {
            const criteriaId = getGradingCriteriaId(student, groupData, selectedEpr, selectedTemplate, mode);
            if (criteriaId == null || criteriaId === "") return "";
            return fluencyByKey[`${student.key}_${criteriaId}`] || "";
        },
        [fluencyByKey, groupData, mode, selectedEpr, selectedTemplate]
    );

    const handleFluencyWpmChange = useCallback(
        (student, item, raw) => {
            const criteriaId = getGradingCriteriaId(student, groupData, selectedEpr, selectedTemplate, mode);
            const next = sanitizeBenchmarkValue(item, raw);
            onFluencyChange?.(student.key, next, criteriaId);
        },
        [groupData, mode, onFluencyChange, selectedEpr, selectedTemplate]
    );

    // ── Early returns ──────────────────────────────────────────────────────────
    if (!selectedTemplate) return null;

    if (contentItems.length === 0) {
        return (
            <div className="no-content-available-wrapper">
                <span className="no-content-text">
                    No content items have been defined for reporting for the selected subject for this semester. Please
                    check with your building leadership team who can amend the school template.
                </span>
            </div>
        );
    }

    if (imagesLoading) {
        return (
            <div className="table-loader-container">
                <LoadingSvg2 />
            </div>
        );
    }

    const fluencySelected = activeColumnIndex < fluencyOffset;
    const activeFluencyItem = fluencySelected ? fluencyItems[activeColumnIndex] : null;
    const activeItem = fluencySelected ? {} : contentItems[activeColumnIndex - fluencyOffset] || {};

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <section>
            {/* Active content item description */}
            <div className={classNames("rating-column-description-wrapper", { hide: showComments })}>
                {fluencySelected ? (
                    <FluencyWpmField
                        label={activeFluencyItem.label}
                        showInput={false}
                        stem={getItemStem(activeFluencyItem, resolvedFluencyGrade)}
                        teacherGuidance={activeFluencyItem.teacherGuidance}
                    />
                ) : (
                    <>
                        <h5 className="rating-column-desc-title">Selected Content Item Description:</h5>
                        <div className="rating-column-description">
                            {activeItem.standardName && activeItem.contentType !== "CDESTANDARD" && (
                                <div className="hierarchy-text-wrapper">
                                    <span className="hierarchy-text">Standard:</span>
                                    <span dangerouslySetInnerHTML={{ __html: activeItem.standardName }} />
                                </div>
                            )}
                            {activeItem.gleName && activeItem.contentType !== "GLE" && (
                                <div className="hierarchy-text-wrapper">
                                    <span style={{ marginRight: 8, fontWeight: 600, marginBottom: 4 }}>GLE:</span>
                                    <span dangerouslySetInnerHTML={{ __html: activeItem.gleName }} />
                                </div>
                            )}
                            {activeItem.contentType === "EVIDENCE_OUTCOME_CHILD" && (
                                <div className="hierarchy-text-wrapper">
                                    <span className="hierarchy-text">Parent Evidence Outcome:</span>
                                    <span dangerouslySetInnerHTML={{ __html: activeItem.eoName }} />
                                </div>
                            )}
                            <hr />
                            <div>
                                <strong>Selected Content Item:</strong>
                                <SafeMathHtml
                                    as="p"
                                    cacheKey={`label-${activeItem.templateContentId}`}
                                    html={activeItem.label}
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Navigation buttons */}
            <div className="rating-table-btn-group">
                <ActionButton
                    label={showComments ? "Hide Student Comments" : "Show Student Comments"}
                    onClick={handleToggleComments}
                />
                <div className="column-nav">
                    <ActionButton
                        className="action-button-80"
                        disable={showComments}
                        label="← Prev"
                        onClick={() => setActiveColumnIndex((i) => Math.max(0, i - 1))}
                    />
                    <ActionButton
                        className="action-button-80"
                        disable={showComments}
                        label="Next →"
                        onClick={() =>
                            setActiveColumnIndex((i) => Math.min(contentItems.length + fluencyOffset - 1, i + 1))
                        }
                    />
                </div>
            </div>

            {/* Split table */}
            <div className="ratings-table-wrapper">
                <div className="table-split-wrapper">
                    {/* Fixed: student name columns */}
                    <div className="fixed-columns" ref={fixedRef}>
                        <table className="rating-table">
                            <thead>
                                <tr>
                                    {mode === "GROUPS" && (
                                        <th>
                                            <SortHeader
                                                label="Group Name"
                                                columnKey="groupName"
                                                sortConfig={sortConfig}
                                                onSort={requestSort}
                                            />
                                        </th>
                                    )}
                                    <th />
                                    <th>
                                        <SortHeader
                                            label="Last Name"
                                            columnKey="lastName"
                                            sortConfig={sortConfig}
                                            onSort={requestSort}
                                        />
                                    </th>
                                    <th>
                                        <SortHeader
                                            label="First Name"
                                            columnKey="firstName"
                                            sortConfig={sortConfig}
                                            onSort={requestSort}
                                        />
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {students.map((student) => (
                                    <tr
                                        key={student.key}
                                        className={
                                            student.isShared && student.groupPermission === "VIEW"
                                                ? "view-only-row"
                                                : ""
                                        }
                                    >
                                        {mode === "GROUPS" && (
                                            <td>
                                                <div className="group-cell">
                                                    <b>{groupNameNormalizer(student.groupName)}</b>{" "}
                                                    {student.isShared && (
                                                        <div className="group-text">{student.groupHomeroomTeacher}</div>
                                                    )}
                                                    {student.isShared && student.groupPermission === "VIEW" && (
                                                        <div className="group-text view-text">View Only</div>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                        <td>
                                            <StudentImage
                                                src={studentImages[student.studentNumber]}
                                                firstName={student.firstName}
                                                lastName={student.lastName}
                                            />
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="as-link"
                                                onClick={() => handleOpenStudent(student)}
                                            >
                                                {student.lastName}
                                            </button>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="as-link"
                                                onClick={() => handleOpenStudent(student)}
                                            >
                                                {student.firstName}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Scrollable: rating columns */}
                    <div className="scrollable-columns" ref={scrollRef}>
                        <table className="rating-table" style={showComments ? { width: "100%" } : {}}>
                            <thead>
                                {showComments ? (
                                    <tr>
                                        <th style={{ fontSize: "1rem" }}>Comments</th>
                                    </tr>
                                ) : (
                                    <tr>
                                        {fluencyItems.map((item, index) => {
                                            const headerButton = (
                                                <button
                                                    type="button"
                                                    className="clickable-header"
                                                    onClick={() => setActiveColumnIndex(index)}
                                                >
                                                    {item.label}
                                                </button>
                                            );
                                            return (
                                                <th
                                                    className={index === activeColumnIndex ? "active-rating-col" : ""}
                                                    key={item.id}
                                                    ref={columnRefs.current[index]}
                                                >
                                                    {item.teacherGuidance ? (
                                                        <OverlayTrigger
                                                            overlay={
                                                                <Tooltip id={`fluency-guidance-${item.id}`}>
                                                                    {item.teacherGuidance}
                                                                </Tooltip>
                                                            }
                                                            placement="bottom"
                                                        >
                                                            <span>{headerButton}</span>
                                                        </OverlayTrigger>
                                                    ) : (
                                                        headerButton
                                                    )}
                                                </th>
                                            );
                                        })}
                                        {contentItems.map((item, index) => {
                                            const preview = item.header || "";
                                            const colIndex = index + fluencyOffset;
                                            return (
                                                <th
                                                    key={item.templateContentId}
                                                    className={
                                                        colIndex === activeColumnIndex ? "active-rating-col" : ""
                                                    }
                                                    ref={columnRefs.current[colIndex]}
                                                >
                                                    <button
                                                        type="button"
                                                        className="clickable-header"
                                                        onClick={() => setActiveColumnIndex(colIndex)}
                                                    >
                                                        <SafeMathHtml
                                                            cacheKey={`hdr-${item.templateContentId}`}
                                                            html={preview}
                                                        />
                                                    </button>
                                                    <select
                                                        aria-label={`Bulk Rating for ${preview}`}
                                                        disabled={
                                                            imagesLoading ||
                                                            viewOnlyGroupExists ||
                                                            anyReleasedEprs ||
                                                            !isEprEditable({
                                                                isActiveWindow,
                                                                status: effectiveStatus,
                                                                userDetails
                                                            })
                                                        }
                                                        value=""
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            onBulkRating(
                                                                item.templateContentId,
                                                                val === "__CLEAR__" ? null : val || null
                                                            );
                                                        }}
                                                    >
                                                        <option value="">Rate for All</option>
                                                        {RATING_OPTIONS.map((opt) => (
                                                            <option key={opt} value={opt}>
                                                                {RATING_DISPLAY_LABELS[opt] || opt}
                                                            </option>
                                                        ))}
                                                        <option value="__CLEAR__">Clear Ratings</option>
                                                    </select>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                )}
                            </thead>
                            <RatingGridBody
                                activeColumnIndex={activeColumnIndex}
                                comments={comments}
                                contentItems={contentItems}
                                derivedExistingRatingsData={derivedExistingRatingsData}
                                fluencyItems={fluencyItems}
                                getFluencyWpm={getFluencyWpmForStudent}
                                getGradingCriteriaId={getGradingCriteriaId}
                                groupData={groupData}
                                handleCommentChange={onCommentChange}
                                handleFluencyWpmChange={handleFluencyWpmChange}
                                handleOpenCommentModal={handleOpenCommentModal}
                                handleRatingChange={onRatingChange}
                                hasChanges={hasChanges}
                                imagesLoading={imagesLoading}
                                isActiveWindow={isActiveWindow}
                                mode={mode}
                                ratings={ratings}
                                ratingsLoaded={ratingsLoaded}
                                selectedEpr={selectedEpr}
                                selectedTemplate={selectedTemplate}
                                showComments={showComments}
                                students={students}
                                userDetails={userDetails}
                            />
                        </table>
                    </div>
                </div>
            </div>

            {/* Save button */}
            <div className="save-btn-ratings">
                <ActionButton
                    disable={
                        imagesLoading ||
                        onlyViewOnly ||
                        !isEprEditable({ isActiveWindow, status: effectiveStatus, userDetails })
                    }
                    label="Save"
                    onClick={() => {
                        onSave();
                        // Fluency WPM lives on the comments save path but is edited
                        // outside the comments panel — flush whenever those are dirty,
                        // not only while Show Student Comments is open.
                        if (onSaveComments && (showComments || hasCommentChanges)) onSaveComments();
                    }}
                />
            </div>

            {/* Per-cell comment modal */}
            <CommentModal
                open={commentModalOpen}
                onClose={closeCommentModal}
                onSave={(ctx) => {
                    onSaveComment(ctx);
                    closeCommentModal();
                }}
                context={activeCommentContext}
                setContext={setActiveCommentContext}
                isActiveWindow={isActiveWindow}
                mode={mode}
                selectedEpr={selectedEpr}
                userDetails={userDetails}
            />
        </section>
    );
};

RatingGrid.propTypes = {
    actionWindowsDto: PropTypes.array,
    basePath: PropTypes.string,
    comments: PropTypes.object,
    fluencyByKey: PropTypes.object,
    existingRatingsData: PropTypes.array,
    groupData: PropTypes.array,
    hasChanges: PropTypes.bool,
    hasCommentChanges: PropTypes.bool,
    mode: PropTypes.string,
    onRatingChange: PropTypes.func.isRequired,
    onBulkRating: PropTypes.func.isRequired,
    onCommentChange: PropTypes.func.isRequired,
    onFluencyChange: PropTypes.func,
    onSave: PropTypes.func.isRequired,
    onSaveComments: PropTypes.func,
    onSaveComment: PropTypes.func.isRequired,
    ratings: PropTypes.object,
    ratingsLoaded: PropTypes.bool,
    selectedEpr: PropTypes.object,
    fluencyGradeName: PropTypes.string,
    selectedSchoolLocationKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    selectedTemplate: PropTypes.object,
    token: PropTypes.string.isRequired,
    userDetails: PropTypes.object.isRequired
};

RatingGrid.defaultProps = {
    existingRatingsData: [],
    groupData: [],
    hasChanges: false,
    hasCommentChanges: false,
    mode: "HOMEROOM",
    ratings: {},
    ratingsLoaded: true,
    comments: {},
    fluencyByKey: {},
    selectedEpr: null,
    selectedTemplate: null
};

export default RatingGrid;
